import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useInView } from "framer-motion";
import { useRef } from "react";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import "./Gallery.css";
import { LazyLoadImage } from "react-lazy-load-image-component";

const Gallery = ({ data }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, threshold: 0.2 });
  const [selectedImage, setSelectedImage] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: { duration: 0.5, ease: "easeOut" },
    },
  };

  const openLightbox = (image, index) => {
    setSelectedImage(image);
    setCurrentIndex(index);
  };

  const closeLightbox = () => {
    setSelectedImage(null);
  };

  const nextImage = () => {
    const nextIndex = (currentIndex + 1) % data.photos.length;
    setCurrentIndex(nextIndex);
    setSelectedImage(data.photos[nextIndex]);
  };

  const prevImage = () => {
    const prevIndex =
      currentIndex === 0 ? data.photos.length - 1 : currentIndex - 1;
    setCurrentIndex(prevIndex);
    setSelectedImage(data.photos[prevIndex]);
  };

  return (
    <section className="gallery" id="gallery" ref={ref}>
      <div className="container">
        <motion.div
          className="gallery-content"
          variants={containerVariants}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
        >
          <motion.div className="section-header" variants={itemVariants}>
            <h2 className="section-title">Gallery</h2>
            <div className="section-line"></div>
            <p className="section-subtitle">A glimpse into my journey</p>
          </motion.div>

          <motion.div className="gallery-grid" variants={containerVariants}>
            {data.photos.map((photo, index) => (
              <motion.div
                key={index}
                className="gallery-item"
                variants={itemVariants}
                whileTap={{ scale: 0.95 }}
                onClick={() => openLightbox(photo, index)}
              >
                <div className="gallery-image">
                  <img src={photo.img} alt={`Gallery ${index + 1}`} />
                  <div className="gallery-overlay">
                    <div className="gallery-overlay-content">
                      <span>View Image</span>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>

          {/* Video Section */}
          {data.videos && data.videos.length > 0 && (
            <motion.div className="videos-section" variants={itemVariants}>
              <h3 className="videos-title">Featured Videos</h3>
              <div className="videos-grid">
                {data.videos.map((video, index) => (
                  <motion.div
                    key={index}
                    className="video-item"
                    variants={itemVariants}
                  >
                    <div className="video-wrapper">
                      <iframe
                        src={`https://www.youtube.com/embed/${video.id}`}
                        title={`Video ${index + 1}`}
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      ></iframe>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          )}
        </motion.div>
      </div>

      {/* Lightbox */}
      <LightBox 
        closeLightbox={closeLightbox} 
        nextImage={nextImage} 
        currentIndex={currentIndex} 
        prevImage={prevImage} 
        selectedImage={selectedImage} 
        totalPhotos={data.photos.length} />

    </section>
  );
};

const LightBox = ({selectedImage, closeLightbox, prevImage, nextImage, currentIndex, totalPhotos}) => {
  
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (!selectedImage) return;

      if (event.key === "ArrowLeft") {
        prevImage();
      } else if (event.key === "ArrowRight") {
        nextImage();
      } else if (event.key === "Escape") {
        closeLightbox();
      }
    };

    if (selectedImage) {
      window.addEventListener("keydown", handleKeyDown);
    }

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [selectedImage, currentIndex])
  
  return <AnimatePresence>
    {selectedImage && (
      <motion.div
        className="lightbox"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={closeLightbox}
      >
        <motion.div
          className="lightbox-content"
          initial={{ scale: 0.8 }}
          animate={{ scale: 1 }}
          exit={{ scale: 0.8 }}
          onClick={(e) => e.stopPropagation()}
        >
          <button className="lightbox-close" onClick={closeLightbox}>
            <X size={24} />
          </button>

          <button
            className="lightbox-nav lightbox-prev"
            onClick={prevImage}
          >
            <ChevronLeft size={24} />
          </button>

          <LazyLoadImage
            src={selectedImage.img}
            placeholder={
              <div className="lightbox-content-img">Loading...</div>
            }
            alt="Image Alt"
          />

          <button
            className="lightbox-nav lightbox-next"
            onClick={nextImage}
          >
            <ChevronRight size={24} />
          </button>

          <div className="lightbox-counter">
            {currentIndex + 1} / {totalPhotos}
          </div>
        </motion.div>
      </motion.div>
    )}
  </AnimatePresence>
}

export default Gallery;
